/**
 * Kiosk Inhabitant Logging
 *
 * Day-1 error-legibility surface (see plans/day1-error-legibility.md).
 *
 * Emits a structured `warn` log tagged with a taxonomy inhabitant id
 * (plans/kiosk-failure-taxonomy.ndjson) at the moment a known failure
 * inhabitant is reached. Changes NO behavior — it only describes what
 * happened, so the on-site morning (Day 2) can bucket real failures and
 * answer the driving question: was the error user-created or system-created?
 *
 * PHI: logs the ENTERED identity (firstName/lastName/phone/dob) deliberately —
 * its plausibility against Experity IS the user-error signal — plus the
 * internal join keys (consumerId/userId) to fetch the source-of-truth record.
 * Email/address/insurance member IDs are NOT logged. See the PHI DECISION
 * section of the Day-1 plan for the rationale and BAA caveat.
 */

import { datadogLogs } from "@datadog/browser-logs";

/**
 * Pre-classification of fault ownership, set at the log site where it is known.
 * - "system": a real exception/cause fired (onError with an Error/ResultErr).
 *   The patient did everything right; the machine aliased it. Never user fault.
 * - "ambiguous": no system error — a user typo or a wrong-at-origin record is
 *   still possible. Adjudicate on-site by comparing entered identity to Experity.
 */
export type FaultHint = "system" | "ambiguous";

export interface InhabitantLogInput {
  /** Taxonomy id, e.g. "returningPatient.correctInput.matchedAsNew" */
  inhabitant: string;
  /** Which transition was taken, e.g. "onError" | "default" | "skip" | "multiple_exhausted" */
  branch: string;
  /** The match-result discriminant string actually received, if any */
  discriminant?: string;
  /** Cause class, e.g. "fetch_timeout" | "network" | "unknown_discriminant" | "user_skip" */
  cause?: string;
  faultHint: FaultHint;

  // --- entered identity (subset PHI — the plausibility signal) ---
  firstNameEntered?: string;
  lastNameEntered?: string;
  phoneRaw?: string;
  phoneNormalized?: string;
  dobEntered?: string;

  // --- internal join keys (look up the source-of-truth record) ---
  consumerId?: string;
  userId?: string;

  // --- non-PHI flow context ---
  /** Per-clinic facet — slice failures by location (RUM usr.practiceID is empty in prod) */
  practiceId?: string;
  orgId?: number;
  consumerIdWasSet?: boolean;
  attemptCount?: number;
  reInvoked?: boolean;
  isEmailMatchMode?: boolean;

  // --- disambiguation (HEAVIER PHI — patient-typed answers) ---
  // Only set on disambiguation-resolution logs. `answer` holds the address/email/zip
  // the patient typed to break a multi-match; it is the signal for whether a wrong
  // disambig outcome was user-created (mistyped answer) or system-created. Isolated
  // in its own group so it is easy to find and redact independently of `identity`.
  /** Which field the backend asked to disambiguate on: "address" | "email" | "zip" | ... */
  disambiguationField?: string;
  /** The patient-typed disambiguation answer(s) — PHI beyond the base subset. */
  disambiguationAnswer?: Record<string, unknown>;
}

/**
 * Derive a fault hint + cause class from an XState onError `event.error`.
 * A real Error or a ResultErr means the system failed — never the patient.
 */
export function classifyError(error: unknown): {
  faultHint: FaultHint;
  cause: string;
} {
  if (error instanceof Error) {
    const name = error.name || "Error";
    const msg = error.message?.toLowerCase() ?? "";
    const cause = /timeout|timed out/.test(msg)
      ? "fetch_timeout"
      : /network|fetch|failed to fetch/.test(msg)
        ? "network"
        : name;
    return { faultHint: "system", cause };
  }
  if (
    typeof error === "object" &&
    error !== null &&
    "isErr" in error &&
    (error as { isErr?: unknown }).isErr === true
  ) {
    return { faultHint: "system", cause: "result_error" };
  }
  // No recognizable system error — leave the user-vs-origin call to on-site.
  return { faultHint: "ambiguous", cause: "no_system_error" };
}

/**
 * Emit a structured inhabitant log. Message is `kiosk.inhabitant.<id>` so a
 * Datadog facet on message yields a per-inhabitant frequency map for free.
 *
 * Defaults to "warn" — these carry no behavior implication and should not sit
 * in the `error` alert stream.
 */
export function logInhabitant(
  input: InhabitantLogInput,
  level: "warn" | "info" = "warn",
): void {
  const {
    inhabitant,
    branch,
    discriminant,
    cause,
    faultHint,
    firstNameEntered,
    lastNameEntered,
    phoneRaw,
    phoneNormalized,
    dobEntered,
    consumerId,
    userId,
    practiceId,
    orgId,
    consumerIdWasSet,
    attemptCount,
    reInvoked,
    isEmailMatchMode,
    disambiguationField,
    disambiguationAnswer,
  } = input;
  // Nest into groups so a log line reads as a small tree, not a flat spray.
  // `inhabitant`, `practiceId`, `orgId` + `tags` stay top-level — primary facets.
  datadogLogs.logger[level](`kiosk.inhabitant.${inhabitant}`, {
    inhabitant,
    practiceId,
    orgId,
    classification: { branch, discriminant, cause, faultHint },
    identity: {
      firstNameEntered,
      lastNameEntered,
      phoneRaw,
      phoneNormalized,
      dobEntered,
      consumerId,
      userId,
    },
    flow: { consumerIdWasSet, attemptCount, reInvoked, isEmailMatchMode },
    // Only present on disambiguation-resolution logs.
    ...(disambiguationField || disambiguationAnswer
      ? {
          disambiguation: {
            field: disambiguationField,
            answer: disambiguationAnswer,
          },
        }
      : {}),
    tags: ["KIOSK", "INHABITANT", inhabitant.split(".")[0] ?? "unknown"],
  });
}
